import { useContext } from 'react'
import { t } from '@lingui/core/macro'
import Tooltip from '@mui/material/Tooltip'
import { CvsAutoComplete } from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { useIntegrationAssignment } from '@global/hooks'
import { FolderFilter } from './folder-filter'
import {
  FullWidth,
  NoMarginTypography,
  TypographyBold,
  FullWidthTextField,
} from './styled-components'
import { FilterFieldProps, IntegrationFiltersProps } from './types'
import { templateCopyContext } from '../../../../../context'

export const IntegrationFilters = ({ filters, integrationAssignment }: IntegrationFiltersProps) => {
  const { onChange } = useContext<any>(templateCopyContext)
  const { onIntegrationMetadataChange } = useIntegrationAssignment({
    integrationAssignment,
    onChange,
  })

  function renderFilters() {
    return filters.map(filter => {
      const key = filter.filterKey

      switch (filter.fieldType) {
        case 'dropdown':
          return (
            <DropdownFilter
              filterField={filter}
              onIntegrationChange={onIntegrationMetadataChange}
              key={key}
            />
          )
        case 'text':
          return (
            <TextFilter
              filterField={filter}
              onIntegrationChange={onIntegrationMetadataChange}
              key={key}
            />
          )
        case 'folder':
          return (
            <FolderFilter
              filterField={filter}
              onIntegrationChange={onIntegrationMetadataChange}
              selectedIntegrationAccountId={integrationAssignment.connector_id}
              key={key}
            />
          )
        case 'date':
        default:
          return <div key={key} />
      }
    })
  }

  return (
    <FullWidth>
      <TypographyBold variant="h4">{t`Integration filters`}</TypographyBold>
      <NoMarginTypography variant="body2">
        {t`Apply filters below to limit the data synced to this template`}
      </NoMarginTypography>
      {renderFilters()}
    </FullWidth>
  )
}

const TextFilter = ({
  filterField,
  onIntegrationChange,
}: Omit<FilterFieldProps, 'selectedIntegrationAccountId'>) => {
  const { filter, title, filterKey, description, hasError, errorMessage } = filterField

  const error = hasError ? [errorMessage] : []

  return (
    <>
      <FullWidthTextField
        margin="dense"
        variant="outlined"
        label={title}
        value={filter}
        onChange={e => {
          onIntegrationChange(e.target.value, filterKey)
        }}
        error={error as never[]}
      />
      {description && <NoMarginTypography variant="caption">{description}</NoMarginTypography>}
    </>
  )
}

const DropdownFilter = ({
  filterField,
  onIntegrationChange,
}: Omit<FilterFieldProps, 'selectedIntegrationAccountId'>) => {
  const { isMulti, filter, filterOptions, title, filterKey, description, dropdownConfig } =
    filterField

  let filterValue = filter

  if (isMulti && typeof filter === 'string') {
    filterValue = JSON.parse(filter)
  }

  if (dropdownConfig && dropdownConfig.waitingOnDependancy && dropdownConfig.dependancyTitle) {
    return (
      <Tooltip
        title={`Before selecting this field, please select a field for the ${
          dropdownConfig.dependancyTitle || ''
        } section`}
        placement="top-start"
      >
        <span>
          <CvsAutoComplete
            disabled={dropdownConfig.waitingOnDependancy}
            isLoading={dropdownConfig.isLoading}
            isMulti={isMulti}
            margin="dense"
            options={(filterOptions as never[]) || []}
            label={title}
            selected={formatSelectedOptions(isMulti, filterValue, filterOptions || [])}
            onSelected={value => {
              onIntegrationChange(value, filterKey)
            }}
          />
        </span>
      </Tooltip>
    )
  }

  return (
    <>
      <CvsAutoComplete
        disabled={dropdownConfig?.waitingOnDependancy || false}
        isLoading={dropdownConfig?.isLoading || false}
        isClearable
        isMulti={isMulti}
        margin="dense"
        options={(filterOptions as never[]) || []}
        label={title}
        selected={formatSelectedOptions(isMulti, filterValue, filterOptions || [])}
        onSelected={e => {
          onIntegrationChange(e, filterKey)
        }}
      />
      {description && <NoMarginTypography variant="caption">{description}</NoMarginTypography>}
    </>
  )
}
